import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { filter, reduce } from 'lodash';
import Toggle from 'react-bootstrap-toggle';
import { getTablePreference } from '../../../App/AppReducer';
import GodTable, { CUSTOM_FIELD_TYPES } from '../../../../components/god-table';
import AccountNameRenderer from '../../../../components/table-cells/AccountNameRenderer';
import RevenueRenderer from '../../../../components/table-cells/RevenueRenderer';
import NumberRenderer from '../../../../components/table-cells/NumberRenderer';
import MoneyRenderer from '../../../../components/table-cells/MoneyRenderer';
import ArrayRenderer from '../../../../components/table-cells/ArrayRenderer';
import DateRenderer from '../../../../components/table-cells/DateRenderer';
import SaleRepsNameRenderer from '../../../../components/table-cells/SaleRepsNameRenderer';
import { stringComparator, dateComparator } from '../../../../components/table-cells/helpers';
import { getUser } from '../../../Auth/AuthReducer';
import StatusRowComponent from './total-stats';

const NULL_COLUMN = 'null';

class AccountTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showAllAccounts: true,
    };
    this.onGridReady = this.onGridReady.bind(this);
    this.getExcluedReasonValue = this.getExcluedReasonValue.bind(this);
  }

  onGridReady(params) {
    this.gridApi = params.api;
  }

  getExcluedReasonValue(params) {
    if (params.data && params.data.exclusion) {
      return (params.data.exclusion.reason || null);
    }
    return '';
  }

  getNameColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      rowDrag: true,
      field: 'name',
      cellRenderer: 'accountNameRenderer',
      comparator: stringComparator,
      sort: 'asc',
      cellRendererParams: {
        enableAutoHeight: true,
        column,
      },
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
    };
  }

  getRevenueColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'annual_revenue',
      type: 'numericColumn',
      cellRenderer: 'revenueRenderer',
      cellRendererParams: {
        column,
      },
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
    };
  }

  getFeedbackStatusColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'feedback_status_label',
      enablePivot: false,
      enableRowGroup: true,
      cellRendererParams: {
        column,
        enableAutoHeight: true,
        field: 'feedback_status_label',
      },
      // valueGetter: this.getPricePositionValue,
    };
  }

  getEmployeeColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'number_of_employees',
      type: 'numericColumn',
      cellRenderer: 'numberRenderer',
      enableValue: true,
      cellRendererParams: {
        column,
      },
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
    };
  }

  getIndustryColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'industry_names',
      enableRowGroup: true,
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      cellRendererParams: {
        enableAutoHeight: true,
        column,
      },
      keyCreator: (params) => {
        const vals = params.value;
        if (!vals || (vals && vals.length === 0)) {
          return '--';
        }
        return vals.sort().join(', ');
      },
      cellRendererSelector: (params) => {
        if (!params) {
          return null;
        }
        return {
          component: 'arrayRenderer',
          params: {
            field: 'industry_names',
          },
        };
      },
    };
  }

  getExcludedReasonColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: column.target_field,
      enablePivot: false,
      enableRowGroup: false,
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      valueGetter: this.getExcluedReasonValue,
      cellRendererParams: {
        enableAutoHeight: true,
        column,
      },
    };
  }

  getLastActivityColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'last_activity_date',
      comparator: dateComparator,
      cellRenderer: 'dateRenderer',
      cellRendererParams: {
        format: 'short',
        column,
      },
    };
  }

  getOwnerColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      rowDrag: true,
      enablePivot: true,
      enableRowGroup: true,
      field: 'owner.name',
      cellRendererSelector: (params) => {
        if (!params) {
          return null;
        }
        return {
          component: 'saleRepsNameRenderer',
          params: {
            enableAutoHeight: true,
            column,
          },
        };
      },
    };
  }

  getTTMColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'ttm',
      type: 'numericColumn',
      cellRenderer: 'moneyRenderer',
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
      cellRendererParams: {
        column,
      },
    };
  }

  getLTVColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'ltv',
      type: 'numericColumn',
      cellRenderer: 'moneyRenderer',
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
      cellRendererParams: {
        column,
      },
    };
  }

  getRequestedFeedbackColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      // field: column.target_field,
      field: 'requested_feedbacks',
      cellRenderer: 'requestedFeedbackRenderer',
      cellRendererParams: {
        column,
        isAccount: true,
      },
    };
  }

  getLLVColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'llv',
      type: 'numericColumn',
      cellRenderer: 'moneyRenderer',
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
      cellRendererParams: {
        column,
      },
    };
  }

  getColDefs() {
    const { tableColumns } = this.props;

    return reduce(tableColumns, (columns, tableColumn) => {
      let column = null;
      
      const externalColumn = this.props.getColDefs ? this.props.getColDefs(tableColumn) : null;

      if (externalColumn === NULL_COLUMN) {
        column = null;
      } else if (externalColumn) {
        column = externalColumn;
      } else if (tableColumn.tag === 'name') {
        column = this.getNameColumn(tableColumn);
      } else if (tableColumn.tag === 'revenue') {
        column = this.getRevenueColumn(tableColumn);
      } else if (tableColumn.tag === 'employees') {
        column = this.getEmployeeColumn(tableColumn);
      } else if (tableColumn.tag === 'industries') {
        if (this.props.tableName === 'dashboard-accounts') {
          column = this.getIndustryColumn(tableColumn);
        }
      } else if (tableColumn.tag === 'surveys') {
        // column = this.getRequestedFeedbackColumn(tableColumn);
      } else if (tableColumn.tag === 'lastActivity') {
        // column = this.getLastActivityColumn(tableColumn);
      } else if (tableColumn.tag === 'ttm') {
        column = this.getTTMColumn(tableColumn);
      } else if (tableColumn.tag === 'ltv') {
        column = this.getLTVColumn(tableColumn);
      } else if (tableColumn.tag === 'llv') {
        column = this.getLLVColumn(tableColumn);
      } else if (tableColumn.tag === 'excludedReason') {
        if (this.props.showExcluedReason) {
          column = this.getExcludedReasonColumn(tableColumn);
        }
      } else if (tableColumn.tag === 'feedbackStatus') {
        column = this.getFeedbackStatusColumn(tableColumn);
      } else if (tableColumn.tag === 'owner') {
        column = this.getOwnerColumn(tableColumn);
      } else {
        column = {
          column: tableColumn,
          customFieldType: CUSTOM_FIELD_TYPES.ACCOUNT,
        };
      }
      if (column) {
        columns.push(column);
      }
      return columns;
    }, []);
  }

  renderManagerFilter() {
    const { isManager } = this.props;
    const { showAllAccounts } = this.state;
    if (!isManager) {
      return null;
    }
    return (
      <span>
        Show&nbsp;
        <Toggle
          onClick={() => {
            this.setState({ showAllAccounts: !showAllAccounts });
          }}
          on={<span>All Accounts</span>}
          off={<span>My Accounts</span>}
          size="xs"
          onstyle="success"
          active={showAllAccounts}
        />
      </span>
    );
  }

  render() {
    const {
      accounts, isManager, auth,
      tableName, filterAccounts,
    } = this.props;
    const { showAllAccounts } = this.state;
    if (accounts.length === 0) {
      return (
        <div className="alert alert-danger">
          No Records Found.
        </div>
      );
    }

    let items = accounts;
    if (isManager && !showAllAccounts) {
      const userId = auth.user_id;
      items = filter(items, { owner_id: userId });
    }

    if (filterAccounts) {
      items = filterAccounts(items);
    }

    const columnDefs = [
      ...this.getColDefs(),
      ...this.props.columns,
    ];
    return (
      <div>
        <GodTable
          id={tableName}
          columnDefs={columnDefs}
          rowData={items}
          onGridReady={this.onGridReady}
          sideBar={{
            // toolPanels: ['columns'],
          }}
          frameworkComponents={{
            accountNameRenderer: AccountNameRenderer,
            revenueRenderer: RevenueRenderer,
            numberRenderer: NumberRenderer,
            arrayRenderer: ArrayRenderer,
            dateRenderer: DateRenderer,
            moneyRenderer: MoneyRenderer,
            agTotalRowCountComponent: StatusRowComponent,
            saleRepsNameRenderer: SaleRepsNameRenderer,
            ...this.props.frameworkComponents,
          }}
          columnTypes={{
            dateColumn: {
              filter: 'agDateColumnFilter',
              filterParams: {
                comparator: dateComparator,
              },
            },
          }}
          getRowNodeId={d => d.account_id}
          leftControls={(this.props.leftControls || this.renderManagerFilter())}
          enableFullScreen
          fullScreenMode={this.props.fullScreenMode}
          fullScreenOffset={this.props.fullScreenOffset}
          tableManageConfigs={{
            validObjects: ['Account'],
            validCustomMetaObjects: ['Account'],
            enableCustomMeta: true,
          }}
          resourceType={GodTable.RESOURCE_TYPE.ACCOUNT}
          onExitFullScreen={this.props.onExitFullScreen}
          onEnterFullScreen={this.props.onEnterFullScreen}
        />
      </div>
    );
  }
}

AccountTable.propTypes = {
  accounts: PropTypes.arrayOf(PropTypes.shape({})),
  isManager: PropTypes.bool,
  auth: PropTypes.shape({}),
  tableColumns: PropTypes.arrayOf(PropTypes.shape({})),
  onEnterFullScreen: PropTypes.func,
  onExitFullScreen: PropTypes.func,
  fullScreenMode: PropTypes.bool,
  fullScreenOffset: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  tableName: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({})),
  frameworkComponents: PropTypes.shape({}),
  showExcluedReason: PropTypes.bool,
  leftControls: PropTypes.node,
  filterAccounts: PropTypes.func,
  getColDefs: PropTypes.func,
};
AccountTable.defaultProps = {
  accounts: [],
  isManager: false,
  auth: null,
  tableColumns: [],
  onEnterFullScreen: () => {},
  onExitFullScreen: () => {},
  fullScreenMode: false,
  fullScreenOffset: { x: 0, y: 0 },
  tableName: 'dashboard-accounts',
  columns: [],
  frameworkComponents: {},
  showExcluedReason: false,
  leftControls: null,
  filterAccounts: null,
  getColDefs: () => { return null },
};
AccountTable.contextTypes = {
};


// Retrieve data from store as props
function mapStateToProps(state, owneProps) {
  return {
    auth: getUser(state),
    tableColumns: getTablePreference(state, owneProps.tableName),
  };
}

export default connect(mapStateToProps)(AccountTable);
